import cx from 'classnames';
import styles from './HeroModuleImage.module.scss';

import React from 'react';

import { HeroImageVariant, TextAlignment, VerticalPosition } from 'api/contentful/contentTypes.cloverdotcom';
import { Grid } from 'components/Grid/Grid';
import { Image } from 'components/Image/Image';
import type { Image as ImageType } from 'services/cms/typesUtil';
import { HERO_MODULE_IMAGE_TEST_ID } from 'components/HeroModuleV2/HeroModuleImage/HeroModuleImage.const';

type HeroModuleImageProps = {
  imageLayout?: string;
  primaryImage?: ImageType;
  productImage?: ImageType;
  secondaryImage?: ImageType;
  fullBleedImage?: ImageType;
  horizontalPosition?: TextAlignment;
  verticalPosition?: VerticalPosition;
};

export const HeroModuleImage: React.FC<HeroModuleImageProps> = ({
  imageLayout,
  primaryImage,
  productImage,
  secondaryImage,
  fullBleedImage,
  horizontalPosition,
  verticalPosition,
}) => {
  const isHeroLayout = primaryImage && (imageLayout === HeroImageVariant.HERO || !imageLayout);
  const isDeviceLayout = primaryImage && productImage && imageLayout === HeroImageVariant.DEVICE;
  const isGalleryLayout = primaryImage && secondaryImage && imageLayout === HeroImageVariant.GALLERY;
  const isFullBleedLayout = fullBleedImage && imageLayout === HeroImageVariant.FULLBLEED;
  const shouldRenderPrimaryImage = isHeroLayout || isDeviceLayout || isGalleryLayout;

  const primaryImageClass = cx({
    [styles.containerHeroLayout]: isHeroLayout,
    [styles.containerDeviceLayout]: isDeviceLayout,
    [styles.containerGalleryLayout]: isGalleryLayout,
  });

  const containerClassNames = cx(
    {
      [styles.containerRight]: horizontalPosition === TextAlignment.RIGHT,
      [styles.containerTop]: verticalPosition === VerticalPosition.TOP,
    },
    styles.container,
  );

  return !isFullBleedLayout ? (
    <Grid item xs={12} lg={5} className={containerClassNames} data-testid={HERO_MODULE_IMAGE_TEST_ID.image}>
      {shouldRenderPrimaryImage && (
        <div className={primaryImageClass}>
          <Image
            src={primaryImage?.file?.url}
            className={styles.containerPrimaryImage}
            alt={primaryImage?.description}
          />
        </div>
      )}
      {isGalleryLayout && (
        <Image
          src={secondaryImage?.file?.url}
          className={styles.containerSecondaryImage}
          alt={secondaryImage?.description}
        />
      )}
      {isDeviceLayout && (
        <Image
          src={productImage?.file?.url}
          className={styles.containerProductImage}
          alt={productImage?.description}
        />
      )}
    </Grid>
  ) : (
    <Image
      src={fullBleedImage?.file?.url}
      className={styles.containerFullBleedImage}
      alt={fullBleedImage?.description}
    />
  );
};

HeroModuleImage.displayName = 'HeroModuleImage';
